import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PatientSignUpForm, PatientSignUpRequestDTO, PatientEnroll } from '@types';
import { useSelector } from 'react-redux';
import '../SignUp.css';
import { RootState } from 'store/store';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { clearError } from 'store/reducers/auth';
import { enrollPatient, registerPatient, requestVerifyCode, verifyCode } from 'store/actions/auth';
import { showErrorToast, showSuccessToast } from 'utils/toastUtils';

interface PatientEnrollInputs extends PatientEnroll { }

interface PatientEnrollComponentProps {
  code?: string;
}

const validationRules = {
  name: {
    required: '이름을 입력하세요.',
  },
  gender: {
    required: '성별을 선택하세요.',
  },
  birthDate: {
    required: '생년월일을 입력하세요.',
  },
  phoneNumber: {
    required: '휴대전화 번호를 입력하세요.',
    pattern: {
      value: /^[0-9-]*$/,
      message: '숫자만 입력 가능합니다.',
    },
  },
  hospital: {
    required: '병원을 선택하세요.',
  },
};

const PatientEnrollComponent: React.FC<PatientEnrollComponentProps> = () => {

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const error = useSelector((state: RootState) => state.user.error);
  const hospitals = useSelector((state: RootState) => state.hospital.hospitals);
  const [isCodeRequested, setIsCodeRequested] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const phiCode = useSelector((state: RootState) => state.user.phiCode);
  const hospital = useSelector((state: RootState) => state.user.user?.hospital);

  const { register, handleSubmit, formState: { errors }, getValues } = useForm<PatientEnrollInputs>({
    mode: 'onChange',
  });

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const onSubmit: SubmitHandler<PatientEnrollInputs> = async (data) => {

    try {
      const signUpData: PatientEnroll = {
        name: data.name,
        hospital: data.hospital,
        phoneNumber: data.phoneNumber,
        gender: data.gender,
        birthDate: data.birthDate,
      };
      const result = await dispatch(enrollPatient(signUpData));

      navigate('/', { state: { 
        successMessage: `등록 완료되었습니다.` } });
    } catch (error) {
      console.error('환자 등록 실패:', error);
    }
  };

  const formatPhoneNumber = (value: string) => {
    if (value.length <= 3) return value;
    if (value.length <= 7) return `${value.slice(0, 3)}-${value.slice(3)}`;
    return `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7, 11)}`;
  };

  return (
    <div className="register-container layoutContainer">
      <div className="register-box">
        <h2>환자 등록</h2>
        {error && <p className="error-message">{error}</p>}

        <div className="input-group">
          <label>병원</label>
          <select
            id="hospital"
            {...register('hospital', validationRules.hospital)}
            value={hospital?.hspCd || ''}
            disabled
          >
            <option value="">병원을 선택하세요</option>
            {hospitals.map(hospital => (
              <option key={hospital.hspCd} value={hospital.hspCd}>
                {hospital.name}
              </option>
            ))}
          </select>
          {errors.hospital && <p className="error-message">{errors.hospital.message}</p>}
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>

        {/* <div className="input-group">
            <label>이메일 *</label>
            <div className="email-container">
              <input
                type="text"
                {...register('email', validationRules.email)}
                placeholder="이메일을 입력하세요"
              />

              {!isCodeRequested ? (
                <button
                  type="button"
                  onClick={handleRequestVerification}
                  className="MainBtncolor verify"
                >
                  인증
                </button>
              ) : null}
            </div>
          </div>
          {!isCodeRequested ? null : (
            <VerificationCode
              onResend={handleRequestVerification}
              onVerify={handleVerifyCode}
              isVerified={isVerified}
            />
          )} */}
          <div className="input-group">
            <label>이름 *</label>
            <input
              {...register('name', validationRules.name)}
              placeholder="이름을 입력하세요"
            />
            {errors.name && <p className="error-message">{errors.name.message}</p>}
          </div>

          <div className="input-group">
            <label>성별 *</label>
            <select {...register('gender', validationRules.gender)}>
              <option value="">성별 선택</option>
              <option value="male">남자</option>
              <option value="female">여자</option>
            </select>
            {errors.gender && <p className="error-message">{errors.gender.message}</p>}
          </div>

          {/* <div className="input-group">
            <label>비밀번호 *</label>
            <input
              type="password"
              {...register('password', validationRules.password)}
              placeholder="비밀번호를 입력하세요"
            />
            {errors.password && <p className="error-message">{errors.password.message}</p>}
          </div>

          <div className="input-group">
            <label>비밀번호 확인 *</label>
            <input
              type="password"
              {...register('confirmPassword', {
                ...validationRules.confirmPassword,
                validate: (value) =>
                  validationRules.confirmPassword.validate(value, () => getValues('password')),
              })}
              placeholder="비밀번호를 다시 입력하세요"
            />
            {errors.confirmPassword && <p className="error-message">{errors.confirmPassword.message}</p>}
          </div> */}

          <div className="input-group">
            <label>생년월일 *</label>
            <input
              type="date"
              max='9999-12-31'
              {...register('birthDate', validationRules.birthDate)}
            />
            {errors.birthDate && <p className="error-message">{errors.birthDate.message}</p>}
          </div>

          <div className="input-group">
            <label>전화번호 *</label>
            <input
              type="tel"
              {...register('phoneNumber', validationRules.phoneNumber)}
              maxLength={14}
              placeholder="전화번호를 입력하세요 (숫자만 입력)"
              onChange={(e) => {
                const formatted = formatPhoneNumber(e.target.value.replace(/[^0-9]/g, ''));
                e.target.value = formatted;
              }}
            />
            {errors.phoneNumber && <p className="error-message">{errors.phoneNumber.message}</p>}
          </div>

          <button type="submit" className="register-button">등록</button>
        </form>
      </div>
    </div>
  );
};

export default PatientEnrollComponent;